import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, ArrowLeft, Share2, RefreshCw } from 'lucide-react';
import URLForm from '../components/URLForm';
import SEOReport from '../components/SEOReport';
import LoadingSpinner from '../components/LoadingSpinner';
import AnalysisProgressDialog from '../components/AnalysisProgressDialog';
import { getDisplayURL } from '../utils/urlUtils';

const AnalysisPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);
  const [analysisCounter, setAnalysisCounter] = useState(0);

  useEffect(() => {
    // Get URL from navigation state or URL params
    const urlFromState = location.state?.url;
    if (urlFromState) {
      setUrl(urlFromState);
      handleAnalysis(urlFromState);
    }
  }, [location]);

  const handleAnalysis = async (targetUrl) => {
    if (!targetUrl) return;

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    setShowProgressDialog(true);
    setAnalysisCounter(prev => prev + 1);

    try {
      console.log('Starting analysis for URL:', targetUrl);
      console.log('Making request to /api/seo/analyze-enhanced');
      
      // First try enhanced analysis
      const response = await fetch('/api/seo/analyze-enhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: targetUrl }),
      });

      console.log('Response received:', response);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Enhanced analysis failed. Status:', response.status);
        console.error('Error response text:', errorText);
        
        // If enhanced analysis fails, try basic analysis as fallback
        if (response.status === 500) {
          console.log('Enhanced analysis failed with 500, trying basic analysis as fallback...');
          
          const basicResponse = await fetch('/api/seo/analyze', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ url: targetUrl }),
          });
          
          if (basicResponse.ok) {
            const basicResult = await basicResponse.json();
            console.log('Basic analysis successful as fallback:', basicResult);
            
            if (basicResult.success) {
              setAnalysisResult({
                ...basicResult,
                message: 'Basic analysis completed (enhanced analysis unavailable)',
                analysisType: 'basic-fallback'
              });
              setUrl(targetUrl);
              return;
            }
          }
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Response JSON parsed:', result);
      
      if (result.success) {
        console.log('Analysis successful, setting result');
        setAnalysisResult(result);
        setUrl(targetUrl);
      } else {
        console.error('Analysis failed with result:', result);
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      console.error('Error details:', err.message);
      
      // Provide more user-friendly error messages
      let userMessage = 'Failed to analyze URL. Please try again.';
      if (err.message.includes('timeout')) {
        userMessage = 'Analysis took too long. Please try again or use a smaller website.';
      } else if (err.message.includes('500')) {
        userMessage = 'Server error occurred. Please try again in a few moments.';
      } else if (err.message.includes('fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(userMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleNewAnalysis = (newUrl) => {
    setUrl(newUrl);
    handleAnalysis(newUrl);
  };

  const handleRetry = () => {
    if (url) {
      handleAnalysis(url);
    }
  };



  const handleShare = async () => {
    if (!analysisResult) return;

    const shareData = {
      title: 'SEO Analysis Report',
      text: `My website scored ${analysisResult.score}/100 (${analysisResult.grade}) on oneclickAIseo.com`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            AI-Powered SEO Analysis
          </h1>
          <p className="text-lg text-gray-600">
            Get comprehensive insights about your website's SEO performance using advanced AI technology
          </p>
        </div>

        {/* URL Form Section */}
        {!analysisResult && !isAnalyzing && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Enter Your Website URL for AI Analysis
            </h2>
            <div className="max-w-2xl mx-auto">
              <URLForm onAnalysisStart={handleNewAnalysis} />
            </div>
          </div>
        )}

        {/* Loading State */}
        {isAnalyzing && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <LoadingSpinner />
            <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">
              AI Analysis in Progress
            </h3>
            <p className="text-gray-600">
              Our advanced AI is crawling your site and analyzing all SEO elements...
            </p>
            <div className="mt-4 text-sm text-gray-500">
              This may take a few moments depending on your site's size and AI processing
            </div>
          </div>
        )}

        {/* Progress Dialog */}
        <AnalysisProgressDialog 
          key={`progress-${analysisCounter}`}
          isVisible={showProgressDialog}
          onComplete={() => setShowProgressDialog(false)}
        />

        {/* Error State */}
        {error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Analysis Failed
              </h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
                <button
                  onClick={() => {
                    setError(null);
                    setAnalysisResult(null);
                  }}
                  className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                >
                  New Analysis
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {analysisResult && (
          <div className="space-y-6">
            {/* Results Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="mb-4 lg:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    AI Analysis Results
                  </h2>
                  <p className="text-gray-600">
                    {getDisplayURL(analysisResult.url)}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleShare}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Results
                  </button>
                </div>
              </div>
            </div>

            {/* SEO Report */}
            <SEOReport report={analysisResult.report} url={analysisResult.url} />

            {/* New Analysis Button */}
            <div className="text-center">
              <button
                onClick={() => {
                  setAnalysisResult(null);
                  setUrl('');
                }}
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-5 h-5 mr-2" />
                Analyze Another Website with AI
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;
